import { useEffect, useMemo, useState } from 'react'
import { ArrowRight, Building2, CheckCircle2, Home } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { INITIAL_PROPERTIES, type PropertyCard, type PropertyLane } from '../lib/operatorWorkspace'
import { cn } from '../lib/utils'

const LANES: Array<{ id: PropertyLane; label: string; color: string }> = [
  { id: 'acquisition', label: 'Acquisition', color: 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/20' },
  { id: 'rehab', label: 'Rehab', color: 'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/20' },
  { id: 'leasing', label: 'Leasing', color: 'border-violet-200 bg-violet-50 dark:border-violet-900 dark:bg-violet-950/20' },
  { id: 'stabilized', label: 'Stabilized', color: 'border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/20' },
]

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)

function nextLane(lane: PropertyLane): PropertyLane {
  const idx = LANES.findIndex((l) => l.id === lane)
  if (idx < 0 || idx === LANES.length - 1) {
    return lane
  }
  return LANES[idx + 1].id
}

type PropertiesPipelineModuleProps = {
  properties?: PropertyCard[]
  onPropertiesChange?: (properties: PropertyCard[]) => void
  addFormRequestId?: number
}

export default function PropertiesPipelineModule({ properties: controlledProperties, onPropertiesChange, addFormRequestId }: PropertiesPipelineModuleProps) {
  const [internalProperties, setInternalProperties] = useState<PropertyCard[]>(INITIAL_PROPERTIES)
  const [notice, setNotice] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newName, setNewName] = useState('')
  const [newAddress, setNewAddress] = useState('')
  const [newManager, setNewManager] = useState('')
  const [newValue, setNewValue] = useState('')
  const [newLane, setNewLane] = useState<PropertyLane>('acquisition')
  const [formError, setFormError] = useState<string | null>(null)
  const [lastRequestId, setLastRequestId] = useState<number | null>(null)
  const properties = controlledProperties ?? internalProperties

  useEffect(() => {
    if (typeof addFormRequestId !== 'number' || addFormRequestId === lastRequestId) {
      return
    }
    setLastRequestId(addFormRequestId)
    setShowAddForm(true)
  }, [addFormRequestId, lastRequestId])

  const updateProperties = (next: PropertyCard[] | ((prev: PropertyCard[]) => PropertyCard[])) => {
    const resolved = typeof next === 'function' ? next(properties) : next
    if (onPropertiesChange) {
      onPropertiesChange(resolved)
      return
    }
    setInternalProperties(resolved)
  }

  const laneGroups = useMemo(() => {
    return LANES.map((lane) => ({
      ...lane,
      cards: properties.filter((p) => p.lane === lane.id),
    }))
  }, [properties])

  const advance = (id: string) => {
    updateProperties((prev) =>
      prev.map((p) => {
        if (p.id !== id) {
          return p
        }
        const next = nextLane(p.lane)
        if (next === p.lane) {
          setNotice(`${p.name} is already stabilized.`)
          return p
        }
        setNotice(`${p.name} moved to ${LANES.find((l) => l.id === next)?.label}.`)
        return { ...p, lane: next }
      }),
    )
  }

  const remove = (id: string) => {
    updateProperties((prev) => prev.filter((property) => property.id !== id))
    setNotice('Property removed from the workspace.')
  }

  const addProperty = () => {
    const trimmedName = newName.trim()
    const trimmedAddress = newAddress.trim()
    const trimmedManager = newManager.trim()
    const parsedValue = Number(newValue)

    if (!trimmedName || !trimmedAddress || !trimmedManager) {
      setFormError('Name, address, and manager are required.')
      return
    }

    if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
      setFormError('Property value must be a positive number.')
      return
    }

    const nextProperty: PropertyCard = {
      id: `property-${Date.now()}`,
      name: trimmedName,
      address: trimmedAddress,
      manager: trimmedManager,
      value: parsedValue,
      lane: newLane,
    }

    updateProperties((prev) => [nextProperty, ...prev])
    setNotice(`${nextProperty.name} added to ${LANES.find((lane) => lane.id === newLane)?.label}.`)
    setShowAddForm(false)
    setFormError(null)
    setNewName('')
    setNewAddress('')
    setNewManager('')
    setNewValue('')
    setNewLane('acquisition')
  }

  return (
    <div className="space-y-4" data-testid="properties-kanban">
      <div>
        <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">Properties</h1>
        <p className="text-xs text-slate-500 dark:text-slate-400">Kanban-style lifecycle board inspired by n8n visual workflows.</p>
      </div>

      <Card className="shadow-sm">
        <CardContent className="space-y-3 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Property CRUD</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Add or remove properties from the shared workspace.</p>
            </div>
            <Button size="sm" variant="secondary" onClick={() => setShowAddForm((prev) => !prev)}>
              {showAddForm ? 'Close' : 'Add Property'}
            </Button>
          </div>

          {showAddForm && (
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-5">
              <Input aria-label="Property Name" placeholder="123 Main St" value={newName} onChange={(event) => setNewName(event.target.value)} />
              <Input aria-label="Property Address" placeholder="123 Main St, Austin TX" value={newAddress} onChange={(event) => setNewAddress(event.target.value)} />
              <Input aria-label="Property Manager" placeholder="Alex Rivera" value={newManager} onChange={(event) => setNewManager(event.target.value)} />
              <Input aria-label="Property Value" inputMode="numeric" placeholder="250000" value={newValue} onChange={(event) => setNewValue(event.target.value)} />
              <select aria-label="Property Lane" className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm shadow-sm dark:border-slate-700 dark:bg-slate-900" value={newLane} onChange={(event) => setNewLane(event.target.value as PropertyLane)}>
                {LANES.map((lane) => (
                  <option key={lane.id} value={lane.id}>{lane.label}</option>
                ))}
              </select>
              <div className="md:col-span-2 xl:col-span-5 flex flex-wrap items-center gap-2">
                <Button size="sm" className="bg-violet-600 text-white hover:bg-violet-700" onClick={addProperty}>Save Property</Button>
                <Button size="sm" variant="ghost" onClick={() => { setShowAddForm(false); setFormError(null) }}>Cancel</Button>
                {formError && <p className="text-xs text-rose-600 dark:text-rose-400">{formError}</p>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {notice && (
        <Card className="border-emerald-200 bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/25">
          <CardContent className="py-2 text-sm text-emerald-800 dark:text-emerald-300">{notice}</CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <Card className="p-3 shadow-sm" data-testid="property-kpi-total-properties">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Total Properties</p>
          <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{properties.length}</p>
        </Card>
        <Card className="p-3 shadow-sm" data-testid="property-kpi-portfolio-value">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Portfolio Value</p>
          <p className="mt-1 text-2xl font-bold text-violet-700 dark:text-violet-400">{fmt(properties.reduce((s, p) => s + p.value, 0))}</p>
        </Card>
        <Card className="p-3 shadow-sm" data-testid="property-kpi-in-rehab">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">In Rehab</p>
          <p className="mt-1 text-2xl font-bold text-amber-700 dark:text-amber-400">{properties.filter((p) => p.lane === 'rehab').length}</p>
        </Card>
        <Card className="p-3 shadow-sm" data-testid="property-kpi-stabilized">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Stabilized</p>
          <p className="mt-1 text-2xl font-bold text-emerald-700 dark:text-emerald-400">{properties.filter((p) => p.lane === 'stabilized').length}</p>
        </Card>
      </div>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Property Lifecycle Kanban</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-4">
            {laneGroups.map((lane, laneIdx) => (
              <div key={lane.id} className={cn('rounded-xl border p-2.5', lane.color)}>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200">{lane.label}</p>
                  <span className="rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-bold text-slate-600 dark:bg-slate-900/80 dark:text-slate-300">{lane.cards.length}</span>
                </div>
                <div className="space-y-2">
                  {lane.cards.map((property) => (
                    <div key={property.id} className="rounded-lg border border-white/70 bg-white/90 p-2.5 shadow-sm dark:border-slate-700 dark:bg-slate-900/90">
                      <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{property.name}</p>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-[10px] text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:text-rose-400 dark:hover:bg-rose-950/30"
                          data-testid={`property-remove-${property.id}`}
                          onClick={() => remove(property.id)}
                        >
                          Remove
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">{property.address}</p>
                      <p className="mt-1 text-xs text-slate-500">Manager: {property.manager}</p>
                      <div className="mt-1 flex items-center justify-between text-xs">
                        <span className="font-semibold text-slate-700 dark:text-slate-200">{fmt(property.value)}</span>
                        {lane.id === 'stabilized' ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                        ) : (
                          <Button data-testid={`property-advance-${property.id}`} size="sm" className="h-6 px-2 text-[10px] bg-violet-600 hover:bg-violet-700 text-white" onClick={() => advance(property.id)}>
                            Advance
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {lane.cards.length === 0 && (
                    <div className="rounded-lg border border-dashed border-slate-300 px-3 py-4 text-center text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">No properties</div>
                  )}
                </div>
                {laneIdx < laneGroups.length - 1 && (
                  <div className="mt-2 flex justify-center xl:hidden">
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardContent className="py-3 text-xs text-slate-600 dark:text-slate-300">
          <div className="flex items-center gap-2">
            <Home className="h-3.5 w-3.5 text-violet-600" />
            <Building2 className="h-3.5 w-3.5 text-emerald-600" />
            Visual lane progression keeps acquisition, rehab, leasing, and stabilization status obvious for operators.
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
